import { useApiClient } from "@/hooks/useApiClient";
import useSWR, { type SWRResponse } from "swr";
import { type ZenrinOAuthToken } from "./useZenrinSearch";

// 5分（ミリ秒）
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

interface ZenrinOAuthTokenResult {
  accessToken: string;
  expiredAt: string;
}

export interface UseZenrinOAuthTokenReturn {
  token: string | undefined;
  refreshToken: () => Promise<void>;
}

/**
 * Zenrin OAuthトークンの取得と期限管理を行うカスタムフック（SWR版）
 * 5分ごとに自動更新を行う
 * @param canUseZenrinMap Zenrinマップを使用可能かどうか
 * @param forceRefresh trueの場合、キャッシュを無視して新しいトークンを取得
 * @returns OAuthトークンと再取得用関数
 */
export const useZenrinOAuthToken = (
  canUseZenrinMap: boolean,
  forceRefresh?: boolean
): UseZenrinOAuthTokenReturn => {
  const { apiClient } = useApiClient();

  const fetcher = async (
    _key: string,
    { arg }: { arg?: { forceRefresh?: boolean } } = {}
  ): Promise<ZenrinOAuthTokenResult | null> => {
    const shouldForceRefresh = arg?.forceRefresh ?? forceRefresh;

    // LocalStorageからキャッシュされたトークンをチェック
    const expiredAtStr = localStorage.getItem("zenrinOauthTokenExpiredAt");
    const currentToken = localStorage.getItem("zenrinOAuthToken");

    // 強制リフレッシュでない場合、トークンが存在し期限内であればキャッシュを使用
    if (
      !shouldForceRefresh &&
      currentToken &&
      expiredAtStr &&
      new Date(expiredAtStr) > new Date()
    ) {
      return {
        accessToken: currentToken,
        expiredAt: expiredAtStr,
      };
    }

    // 新しいトークンを取得
    try {
      const response = await apiClient.post<ZenrinOAuthToken>(
        "/zenrin/oauth/token"
      );
      const token = response.data;

      if (token) {
        localStorage.setItem("zenrinOAuthToken", token.accessToken);
        localStorage.setItem("zenrinOauthTokenExpiredAt", token.expiredAt);
        return {
          accessToken: token.accessToken,
          expiredAt: token.expiredAt,
        };
      }
      return null;
    } catch (_error) {
      return null;
    }
  };

  // canUseZenrinMapがfalseの場合はSWRキーをnullにして取得を無効化
  const { data, mutate }: SWRResponse<ZenrinOAuthTokenResult | null> = useSWR(
    canUseZenrinMap ? "zenrin-oauth-token" : null,
    fetcher,
    {
      refreshInterval: REFRESH_INTERVAL_MS,
      revalidateOnFocus: true,
      shouldRetryOnError: false,
    }
  );

  const refreshToken = async (): Promise<void> => {
    // LocalStorageのトークン期限を過去に設定してキャッシュを無効化
    localStorage.setItem("zenrinOauthTokenExpiredAt", "1970-01-01T00:00:00Z");
    // SWRのキャッシュも無効化して強制的に再取得
    await mutate(undefined, { revalidate: true });
  };

  return {
    token: data?.accessToken,
    refreshToken,
  };
};
